export type CrmContactHealthTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmContactHealthMetric {
  key: string;
  label: string;
  value: string;
  description: string | null;
  weight: string | null;
  tone: CrmContactHealthTone;
}

export interface CrmContactHealthSnapshot {
  key: string;
  label: string;
  score: number | null;
  status: string | null;
  summary: string | null;
  updatedAt: string | null;
  tone: CrmContactHealthTone;
  metrics: CrmContactHealthMetric[];
}

export interface BuildCrmContactHealthSnapshotOptions {
  snapshotKeys?: readonly string[];
  maxMetrics?: number;
}

export const CRM_CONTACT_HEALTH_SNAPSHOT_FIELD_KEYS = [
  "crmContactHealthSnapshot",
  "contactHealthSnapshot",
  "customerHealthSnapshot",
  "healthSnapshot",
  "customerHealth",
  "retentionHealth",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 180;

export function buildCrmContactHealthSnapshot(
  customFields: unknown,
  options: BuildCrmContactHealthSnapshotOptions = {},
): CrmContactHealthSnapshot | null {
  const fields = asRecord(customFields);
  if (!fields) return null;

  const snapshotKeys = options.snapshotKeys?.length
    ? [...options.snapshotKeys]
    : [...CRM_CONTACT_HEALTH_SNAPSHOT_FIELD_KEYS];
  const maxMetrics = normalizePositiveInteger(options.maxMetrics, 8);

  for (const key of snapshotKeys) {
    const snapshot = parseHealthSnapshot(key, fields[key], maxMetrics);
    if (snapshot) return snapshot;
  }

  return null;
}

function parseHealthSnapshot(
  fallbackKey: string,
  value: unknown,
  maxMetrics: number,
): CrmContactHealthSnapshot | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const score = normalizeScore(
    record.score
    ?? record.healthScore
    ?? record.compositeScore
    ?? record.customerHealthScore
    ?? record.retentionScore,
  );
  const status = truncate(
    readString(record.status)
    ?? readString(record.healthStatus)
    ?? readString(record.state)
    ?? readString(record.band),
  );
  const summary = truncate(
    readString(record.summary)
    ?? readString(record.description)
    ?? readString(record.evidence),
  );
  const updatedAt = normalizeTimestamp(
    readString(record.updatedAt)
    ?? readString(record.computedAt)
    ?? readString(record.calculatedAt)
    ?? readString(record.timestamp)
    ?? readString(record.at),
  );
  const metrics = parseHealthMetrics(
    record.metrics
    ?? record.components
    ?? record.dimensionScores
    ?? record.scores
    ?? record.signals
    ?? record,
    maxMetrics,
  );

  if (score === null && !status && !summary && metrics.length === 0) return null;

  return {
    key,
    label: truncate(readString(record.label) ?? readString(record.title) ?? formatLabel(key)) ?? formatLabel(key),
    score,
    status,
    summary,
    updatedAt,
    tone: normalizeTone(readString(record.tone) ?? status),
    metrics,
  };
}

function parseHealthMetrics(value: unknown, maxMetrics: number): CrmContactHealthMetric[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseHealthMetric(`metric-${index + 1}`, entry))
      .filter((metric): metric is CrmContactHealthMetric => Boolean(metric))
      .slice(0, maxMetrics);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredSnapshotKeys = new Set([
    "key",
    "label",
    "title",
    "score",
    "healthScore",
    "compositeScore",
    "customerHealthScore",
    "retentionScore",
    "status",
    "healthStatus",
    "state",
    "band",
    "summary",
    "description",
    "evidence",
    "updatedAt",
    "computedAt",
    "calculatedAt",
    "timestamp",
    "at",
    "tone",
    "metrics",
    "components",
    "dimensionScores",
    "scores",
    "signals",
  ]);

  return Object.entries(record)
    .filter(([key]) => !ignoredSnapshotKeys.has(key))
    .map(([key, entry]) => parseHealthMetric(key, entry))
    .filter((metric): metric is CrmContactHealthMetric => Boolean(metric))
    .slice(0, maxMetrics);
}

function parseHealthMetric(fallbackKey: string, value: unknown): CrmContactHealthMetric | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = record
    ? truncate(readString(record.label) ?? readString(record.title) ?? formatLabel(key)) ?? formatLabel(key)
    : formatLabel(key);
  const rawValue = record && "value" in record
    ? record.value
    : record?.score ?? record?.percent ?? record?.status ?? value;
  const formattedValue = formatHealthValue(rawValue);
  if (!formattedValue) return null;

  return {
    key,
    label,
    value: formattedValue,
    description: record ? truncate(readString(record.description) ?? readString(record.helpText) ?? readString(record.evidence)) : null,
    weight: record ? truncate(readString(record.weight)) : null,
    tone: normalizeTone(record ? readString(record.tone) ?? readString(record.status) : null),
  };
}

function normalizeScore(value: unknown): number | null {
  const numeric = typeof value === "number"
    ? value
    : typeof value === "string" && value.trim()
      ? Number.parseFloat(value.trim().replace(/%$/, ""))
      : NaN;
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

function normalizeTimestamp(value: string | null): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString();
}

function normalizeTone(value: string | null): CrmContactHealthTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "critical" || value === "at_risk" || value === "risk" || value === "churn_risk" || value === "overdue") return "danger";
  if (value === "onboarding" || value === "pending" || value === "needs_attention" || value === "watch") return "warning";
  if (value === "thriving" || value === "healthy" || value === "active" || value === "good") return "success";
  return "neutral";
}

function formatHealthValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(Math.round(value * 100) / 100) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}
